import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2,
  CheckCircle2,
  Clock,
  Loader2,
  MapPin,
  Package,
  Phone,
  Plus,
  Search,
  Truck,
  UserRound,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useFarmaciaAuth } from "../../context/FarmaciaAuthContext";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Checkbox } from "../../components/ui/checkbox";
import {
  farmGet,
  farmPatch,
  farmPost,
  STATO_CONSEGNA_LABELS,
  FASCIA_CONSEGNA_LABELS,
} from "./shared";
import { FarmaciaPageChrome } from "../../components/FarmaciaPageChrome";

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function formatDateLabel(iso) {
  if (!iso) return "—";
  try {
    return new Date(`${iso}T12:00:00`).toLocaleDateString("it-IT", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso).slice(0, 16);
  }
}

function shortId(id) {
  if (!id) return "—";
  const s = String(id);
  return s.length > 12 ? `CON-${s.slice(0, 8).toUpperCase()}` : s;
}

function shortOrdine(id) {
  if (!id) return null;
  const s = String(id);
  return s.length > 10 ? `#ORD-${s.slice(0, 6).toUpperCase()}` : `#${s}`;
}

const IN_PROGRAMMAZIONE = new Set(["da_pianificare", "pianificata", "pronta", "in_preparazione"]);
const IN_CONSEGNA = new Set(["in_consegna"]);
const CONSEGNATE = new Set(["consegnata", "parziale"]);
const NON_CONSEGNATE = new Set(["problema", "riprogrammata"]);

function KpiTile({ icon: Icon, label, value, hint, tone }) {
  const tones = {
    sky: "bg-sky-50 text-sky-700",
    amber: "bg-amber-50 text-amber-700",
    blue: "bg-blue-50 text-blue-700",
    emerald: "bg-emerald-50 text-emerald-700",
    rose: "bg-rose-50 text-rose-700",
  };
  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white p-3.5 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[11px] font-medium text-slate-500">{label}</p>
          <p className="mt-0.5 text-xl font-semibold tracking-tight text-slate-900">{value}</p>
          {hint ? <p className="mt-0.5 text-[10px] text-slate-400">{hint}</p> : null}
        </div>
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${tones[tone] || tones.sky}`}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
    </div>
  );
}

function Timeline({ stato }) {
  const steps = [
    { id: "prog", label: "In programmazione", match: IN_PROGRAMMAZIONE },
    { id: "run", label: "In consegna", match: IN_CONSEGNA },
    { id: "done", label: "Consegnata", match: new Set(["consegnata", "parziale"]) },
    { id: "fail", label: "Non consegnata", match: NON_CONSEGNATE },
  ];
  let currentIdx = steps.findIndex((s) => s.match.has(stato));
  if (currentIdx < 0) currentIdx = 0;

  return (
    <ol className="space-y-3">
      {steps.map((s, idx) => {
        const done = idx < currentIdx || (idx === currentIdx && CONSEGNATE.has(stato));
        const active = idx === currentIdx && !CONSEGNATE.has(stato);
        const failed = s.id === "fail" && NON_CONSEGNATE.has(stato);
        return (
          <li key={s.id} className="flex items-start gap-2.5 text-xs">
            <span
              className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
                failed
                  ? "bg-rose-500 text-white"
                  : done
                    ? "bg-emerald-500 text-white"
                    : active
                      ? "bg-sky-500 text-white"
                      : "border border-slate-200 bg-white text-slate-300"
              }`}
            >
              {done || failed ? <CheckCircle2 className="h-3 w-3" /> : <span className="h-1.5 w-1.5 rounded-full bg-current" />}
            </span>
            <span className={active || failed ? "font-semibold text-slate-800" : "text-slate-500"}>
              {s.label}
              {s.id === "done" && stato === "parziale" ? " (parziale)" : ""}
              {s.id === "fail" && stato === "riprogrammata" ? " · riprogrammata" : ""}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function ConsegnaDetailPanel({ c, onClose, onStato, onEsito }) {
  if (!c) {
    return (
      <aside className="hidden w-[340px] shrink-0 xl:block">
        <div className="sticky top-0 rounded-2xl border border-dashed border-slate-200 bg-white/60 p-6 text-center">
          <Truck className="mx-auto mb-2 h-8 w-8 text-slate-300" />
          <p className="text-sm font-medium text-slate-600">Consegna selezionata</p>
          <p className="mt-1 text-xs text-slate-400">Seleziona una riga dall&apos;elenco per il dettaglio.</p>
        </div>
      </aside>
    );
  }

  const st = STATO_CONSEGNA_LABELS[c.stato] || {
    label: c.stato,
    className: "bg-slate-50 text-slate-600 border-slate-200",
  };
  const mapsUrl = c.indirizzo
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(c.indirizzo)}`
    : null;
  const tel = (c.contatto || "").replace(/[^\d+]/g, "");

  return (
    <aside className="w-full shrink-0 xl:w-[340px]">
      <div className="sticky top-0 flex max-h-[calc(100vh-10rem)] flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
        <div className="border-b border-slate-100 p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="font-mono text-xs font-semibold text-slate-500">{shortId(c.id)}</p>
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                <Badge variant="outline" className={`text-[10px] ${st.className}`}>
                  {st.label}
                </Badge>
              </div>
              <p className="mt-1.5 text-[10px] text-slate-400">
                Ultimo aggiornamento: {formatWhen(c.updated_at || c.created_at)}
              </p>
            </div>
            <button
              type="button"
              className="text-xs text-slate-400 hover:text-slate-600 xl:hidden"
              onClick={onClose}
            >
              Chiudi
            </button>
          </div>
        </div>

        <div className="space-y-4 overflow-y-auto p-4 text-xs">
          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Destinatario
            </p>
            <div className="flex items-start gap-2.5">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#0F5C4C] text-sm font-semibold text-white">
                <Building2 className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <p className="font-semibold text-slate-900">{c.struttura_nome || "Struttura"}</p>
                <Badge variant="outline" className="mt-1 border-violet-200 bg-violet-50 text-[10px] text-violet-800">
                  In struttura
                </Badge>
                {c.contatto ? <p className="mt-1 text-slate-500">{c.contatto}</p> : null}
              </div>
            </div>
          </div>

          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Indirizzo di consegna
            </p>
            <p className="flex items-start gap-1.5 text-slate-700">
              <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
              <span>{c.indirizzo || "Indirizzo non disponibile"}</span>
            </p>
            {mapsUrl ? (
              <a
                href={mapsUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-2 inline-flex text-[11px] font-medium text-emerald-700 hover:underline"
              >
                Apri in Maps
              </a>
            ) : null}
          </div>

          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Dettagli consegna
            </p>
            <dl className="space-y-1.5">
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">Ordini</dt>
                <dd className="text-right font-medium text-slate-800">
                  {(c.ordine_ids || []).map(shortOrdine).filter(Boolean).join(", ") || "—"}
                </dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">Data</dt>
                <dd className="font-medium text-slate-800">{c.data || "—"}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">Finestra</dt>
                <dd className="font-medium text-slate-800">
                  {FASCIA_CONSEGNA_LABELS[c.fascia_oraria] || c.fascia_oraria || "—"}
                </dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">Autista</dt>
                <dd className="font-medium text-slate-800">{c.autista || "Non assegnato"}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">Colli</dt>
                <dd className="font-medium text-slate-800">{c.numero_colli ?? 0}</dd>
              </div>
              {c.note_logistiche ? (
                <div className="rounded-lg bg-slate-50 px-2.5 py-2 text-slate-600">
                  Note: {c.note_logistiche}
                </div>
              ) : null}
            </dl>
          </div>

          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Stato consegna
            </p>
            <Timeline stato={c.stato} />
          </div>

          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Azioni
            </p>
            <div className="flex flex-wrap gap-1.5">
              {["in_preparazione", "in_consegna"].map((s) => (
                <Button
                  key={s}
                  size="sm"
                  variant="outline"
                  className="h-7 rounded-lg text-[11px]"
                  disabled={c.stato === s || ["consegnata", "parziale"].includes(c.stato)}
                  onClick={() => onStato(c.id, s)}
                >
                  {STATO_CONSEGNA_LABELS[s]?.label || s}
                </Button>
              ))}
              <Button
                size="sm"
                className="h-7 rounded-lg bg-[#0F5C4C] text-[11px] hover:bg-[#0c4a3e]"
                disabled={["consegnata"].includes(c.stato)}
                onClick={() => onEsito(c.id, "completa")}
              >
                Completa
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 rounded-lg text-[11px]"
                disabled={["consegnata"].includes(c.stato)}
                onClick={() => onEsito(c.id, "parziale")}
              >
                Parziale
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 rounded-lg text-[11px] text-rose-700"
                onClick={() => onEsito(c.id, "problema")}
              >
                Problema
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 rounded-lg text-[11px]"
                onClick={() => onEsito(c.id, "riprogramma")}
              >
                Riprogramma
              </Button>
            </div>
          </div>
        </div>

        <div className="mt-auto grid grid-cols-2 gap-2 border-t border-slate-100 p-3">
          {tel ? (
            <Button asChild variant="outline" className="h-10 rounded-xl">
              <a href={`tel:${tel}`}>
                <Phone className="mr-1.5 h-3.5 w-3.5" />
                Chiama
              </a>
            </Button>
          ) : (
            <Button variant="outline" className="h-10 rounded-xl" disabled>
              Chiama
            </Button>
          )}
          <Button asChild className="h-10 rounded-xl bg-[#0F5C4C] hover:bg-[#0c4a3e]">
            <Link to="/farmacia/ordini">Dettagli ordine</Link>
          </Button>
        </div>
      </div>
    </aside>
  );
}

export default function ConsegneFarmacia() {
  const { token } = useFarmaciaAuth();
  const [data, setData] = useState(todayISO());
  const [groups, setGroups] = useState([]);
  const [consegne, setConsegne] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedByOrg, setSelectedByOrg] = useState({});
  const [planOpen, setPlanOpen] = useState(false);
  const [planOrg, setPlanOrg] = useState(null);
  const [fascia, setFascia] = useState("mattina");
  const [autista, setAutista] = useState("");
  const [colli, setColli] = useState(1);
  const [noteLog, setNoteLog] = useState("");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("tutte");
  const [q, setQ] = useState("");
  const [autistaFilter, setAutistaFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [pronte, lista] = await Promise.all([
        farmGet("/farmacia/consegne/pronte", token),
        farmGet("/farmacia/consegne", token, { data }),
      ]);
      const items = lista.items || [];
      setGroups(pronte.groups || []);
      setConsegne(items);
      setSelectedId((prev) => {
        if (prev && items.some((c) => c.id === prev)) return prev;
        return items[0]?.id || null;
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento consegne");
    } finally {
      setLoading(false);
    }
  }, [token, data]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleOrdine = (orgId, ordineId) => {
    setSelectedByOrg((prev) => {
      const set = new Set(prev[orgId] || []);
      if (set.has(ordineId)) set.delete(ordineId);
      else set.add(ordineId);
      return { ...prev, [orgId]: Array.from(set) };
    });
  };

  const pianifica = async (group) => {
    const ids = selectedByOrg[group.organization_id] || [];
    if (!ids.length) {
      toast.error("Seleziona almeno un ordine pronto");
      return;
    }
    setBusy(true);
    try {
      await farmPost("/farmacia/consegne", token, {
        organization_id: group.organization_id,
        ordine_ids: ids,
        data,
        fascia_oraria: fascia,
        autista: autista.trim() || undefined,
        numero_colli: Number(colli) || 1,
        note_logistiche: noteLog.trim() || undefined,
        stato: "pianificata",
      });
      toast.success("Consegna pianificata");
      setSelectedByOrg((prev) => ({ ...prev, [group.organization_id]: [] }));
      setPlanOrg(null);
      setNoteLog("");
      setPlanOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Pianificazione fallita");
    } finally {
      setBusy(false);
    }
  };

  const setStato = async (id, stato) => {
    try {
      await farmPatch(`/farmacia/consegne/${encodeURIComponent(id)}`, token, { stato });
      toast.success("Stato aggiornato");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aggiornamento fallito");
    }
  };

  const esito = async (id, tipo) => {
    try {
      const body = { esito: tipo };
      if (tipo === "riprogramma") body.nuova_data = data;
      await farmPost(`/farmacia/consegne/${encodeURIComponent(id)}/esito`, token, body);
      toast.success("Esito registrato");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Esito non registrato");
    }
  };

  const prontiCount = useMemo(
    () => groups.reduce((acc, g) => acc + (g.ordini || []).length, 0),
    [groups]
  );

  const autisti = useMemo(() => {
    const set = new Set();
    for (const c of consegne) {
      if (c.autista) set.add(c.autista);
    }
    return Array.from(set).sort();
  }, [consegne]);

  const kpi = useMemo(() => {
    const inProg = consegne.filter((c) => IN_PROGRAMMAZIONE.has(c.stato)).length;
    const inCons = consegne.filter((c) => IN_CONSEGNA.has(c.stato)).length;
    const done = consegne.filter((c) => CONSEGNATE.has(c.stato)).length;
    const fail = consegne.filter((c) => NON_CONSEGNATE.has(c.stato)).length;
    return {
      totali: consegne.length,
      inProg,
      inCons,
      done,
      fail,
      daAssegnare: consegne.filter((c) => IN_PROGRAMMAZIONE.has(c.stato) && !c.autista).length,
    };
  }, [consegne]);

  const filtered = useMemo(() => {
    let list = consegne;
    if (tab === "in_programmazione") list = list.filter((c) => IN_PROGRAMMAZIONE.has(c.stato));
    if (tab === "in_consegna") list = list.filter((c) => IN_CONSEGNA.has(c.stato));
    if (tab === "consegnate") list = list.filter((c) => CONSEGNATE.has(c.stato));
    if (tab === "non_consegnate") list = list.filter((c) => NON_CONSEGNATE.has(c.stato));
    if (autistaFilter !== "all") list = list.filter((c) => c.autista === autistaFilter);
    const term = q.trim().toLowerCase();
    if (term) {
      list = list.filter((c) => {
        const blob = [
          c.id,
          c.struttura_nome,
          c.indirizzo,
          c.autista,
          c.contatto,
          ...(c.ordine_ids || []),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return blob.includes(term);
      });
    }
    return list;
  }, [consegne, tab, autistaFilter, q]);

  const selected = consegne.find((c) => c.id === selectedId) || null;

  const tabs = [
    { id: "tutte", label: "Tutte", count: kpi.totali },
    { id: "in_programmazione", label: "In programmazione", count: kpi.inProg },
    { id: "in_consegna", label: "In consegna", count: kpi.inCons },
    { id: "consegnate", label: "Consegnate", count: kpi.done },
    { id: "non_consegnate", label: "Non consegnate", count: kpi.fail },
  ];

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5 xl:flex-row xl:items-start">
      <div className="min-w-0 flex-1 space-y-5">
        <FarmaciaPageChrome>
          <Button asChild size="sm" variant="outline" className="rounded-xl">
            <Link to={`/farmacia/autista?data=${encodeURIComponent(data)}`}>
              <UserRound className="mr-1.5 h-4 w-4" />
              Vista autista
            </Link>
          </Button>
        </FarmaciaPageChrome>

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-5">
          <KpiTile icon={Truck} label="Consegne totali" value={kpi.totali} hint={formatDateLabel(data)} tone="sky" />
          <KpiTile
            icon={Clock}
            label="In programmazione"
            value={kpi.inProg}
            hint={kpi.daAssegnare ? `${kpi.daAssegnare} da assegnare` : undefined}
            tone="amber"
          />
          <KpiTile icon={Truck} label="In consegna" value={kpi.inCons} tone="blue" />
          <KpiTile icon={CheckCircle2} label="Consegnate" value={kpi.done} tone="emerald" />
          <KpiTile icon={XCircle} label="Non consegnate" value={kpi.fail} tone="rose" />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Input
            type="date"
            className="h-9 w-auto rounded-xl"
            value={data}
            onChange={(e) => setData(e.target.value)}
          />
          <select
            className="h-9 rounded-xl border border-slate-200 bg-white px-2.5 text-xs text-slate-700"
            value={autistaFilter}
            onChange={(e) => setAutistaFilter(e.target.value)}
          >
            <option value="all">Tutti gli autisti</option>
            {autisti.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
          <div className="relative min-w-[160px] flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cerca consegne…"
              className="h-9 rounded-xl pl-8 text-xs"
            />
          </div>
          <Button
            type="button"
            className="rounded-xl bg-[#0F5C4C] hover:bg-[#0c4a3e]"
            onClick={() => setPlanOpen((v) => !v)}
          >
            <Plus className="mr-1.5 h-4 w-4" />
            Pianifica nuovo giro
            {prontiCount ? (
              <Badge className="ml-2 bg-white/20 text-[10px] text-white hover:bg-white/20">
                {prontiCount}
              </Badge>
            ) : null}
          </Button>
        </div>

        {planOpen ? (
          <section className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
            <div className="mb-3 flex items-center justify-between gap-2">
              <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                <Package className="h-4 w-4 text-emerald-600" />
                Ordini pronti da pianificare
              </h2>
              <Button type="button" variant="ghost" size="sm" onClick={() => setPlanOpen(false)}>
                Chiudi
              </Button>
            </div>
            {groups.length === 0 ? (
              <p className="py-6 text-center text-sm text-slate-500">
                Nessun ordine pronto. Porta gli ordini allo stato «Pronto» da{" "}
                <Link to="/farmacia/ordini" className="font-medium text-emerald-700 hover:underline">
                  Ordini
                </Link>
                .
              </p>
            ) : (
              <div className="space-y-3">
                {groups.map((g) => {
                  const selected = new Set(selectedByOrg[g.organization_id] || []);
                  const planning = planOrg === g.organization_id;
                  return (
                    <div key={g.organization_id || "x"} className="rounded-xl border border-slate-100 p-3">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div>
                          <p className="font-medium text-slate-900">{g.struttura_nome}</p>
                          <p className="text-xs text-slate-500">{g.indirizzo || "Indirizzo non disponibile"}</p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-lg"
                          onClick={() => setPlanOrg(planning ? null : g.organization_id)}
                        >
                          {planning ? "Chiudi form" : "Pianifica"}
                        </Button>
                      </div>
                      <ul className="mt-3 space-y-1">
                        {(g.ordini || []).map((o) => (
                          <li key={o.id}>
                            <label className="flex cursor-pointer items-start gap-2 rounded-md px-1 py-1 hover:bg-slate-50">
                              <Checkbox
                                checked={selected.has(o.id)}
                                onCheckedChange={() => toggleOrdine(g.organization_id, o.id)}
                              />
                              <span className="text-sm text-slate-700">
                                <span className="font-medium">{o.righe_count} prodotti</span>
                                <span className="mt-0.5 block text-xs text-slate-400">
                                  {(o.riepilogo || []).join(", ") || "—"}
                                </span>
                              </span>
                            </label>
                          </li>
                        ))}
                      </ul>
                      {planning ? (
                        <div className="mt-3 flex flex-wrap items-end gap-2 rounded-xl bg-slate-50 p-3">
                          <div>
                            <label className="text-[10px] uppercase text-slate-500">Fascia</label>
                            <select
                              className="mt-1 h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm"
                              value={fascia}
                              onChange={(e) => setFascia(e.target.value)}
                            >
                              {Object.entries(FASCIA_CONSEGNA_LABELS).map(([k, v]) => (
                                <option key={k} value={k}>
                                  {v}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="text-[10px] uppercase text-slate-500">Autista</label>
                            <Input
                              className="mt-1 h-9 w-40"
                              value={autista}
                              onChange={(e) => setAutista(e.target.value)}
                              placeholder="Nome"
                            />
                          </div>
                          <div>
                            <label className="text-[10px] uppercase text-slate-500">Colli</label>
                            <Input
                              type="number"
                              min={0}
                              className="mt-1 h-9 w-20"
                              value={colli}
                              onChange={(e) => setColli(e.target.value)}
                            />
                          </div>
                          <div className="min-w-[160px] flex-1">
                            <label className="text-[10px] uppercase text-slate-500">Note</label>
                            <Input
                              className="mt-1 h-9"
                              value={noteLog}
                              onChange={(e) => setNoteLog(e.target.value)}
                            />
                          </div>
                          <Button
                            className="rounded-xl bg-[#0F5C4C] hover:bg-[#0c4a3e]"
                            disabled={busy}
                            onClick={() => pianifica(g)}
                          >
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crea consegna"}
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        ) : null}

        <div className="flex max-w-full flex-wrap gap-1.5 rounded-xl bg-slate-100/80 p-1">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`rounded-lg px-2.5 py-1.5 text-[11px] font-medium transition sm:text-xs ${
                tab === t.id
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              {t.label}
              <span className="ml-1 text-slate-400">({t.count})</span>
            </button>
          ))}
        </div>

        <section className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
          {filtered.length === 0 ? (
            <div className="px-4 py-16 text-center">
              <Truck className="mx-auto mb-3 h-8 w-8 text-slate-300" />
              <p className="text-sm font-medium text-slate-700">Nessuna consegna</p>
              <p className="mt-1 text-xs text-slate-400">
                Nessuna consegna per {formatDateLabel(data)} con i filtri selezionati.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[860px] text-left text-xs">
                <thead className="border-b border-slate-100 bg-slate-50/80 text-[11px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-2.5 font-semibold">Consegna</th>
                    <th className="px-3 py-2.5 font-semibold">Tipo</th>
                    <th className="px-3 py-2.5 font-semibold">Destinatario</th>
                    <th className="px-3 py-2.5 font-semibold">Indirizzo</th>
                    <th className="px-3 py-2.5 font-semibold">Finestra</th>
                    <th className="px-3 py-2.5 font-semibold">Autista</th>
                    <th className="px-3 py-2.5 font-semibold">Stato</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((c) => {
                    const active = selectedId === c.id;
                    const st = STATO_CONSEGNA_LABELS[c.stato] || {
                      label: c.stato,
                      className: "bg-slate-50 text-slate-600 border-slate-200",
                    };
                    return (
                      <tr
                        key={c.id}
                        className={`cursor-pointer ${active ? "bg-emerald-50/50" : "hover:bg-slate-50/80"}`}
                        onClick={() => setSelectedId(c.id)}
                      >
                        <td className="px-4 py-3">
                          <p className="font-mono text-[11px] font-semibold text-slate-700">
                            {shortId(c.id)}
                          </p>
                          <p className="text-[10px] text-slate-400">
                            {(c.ordine_ids || []).map(shortOrdine).filter(Boolean).join(" · ") || "—"}
                          </p>
                        </td>
                        <td className="px-3 py-3">
                          <Badge
                            variant="outline"
                            className="border-violet-200 bg-violet-50 text-[10px] text-violet-800"
                          >
                            In struttura
                          </Badge>
                        </td>
                        <td className="px-3 py-3 font-medium text-slate-800">
                          {c.struttura_nome || "—"}
                        </td>
                        <td className="max-w-[180px] truncate px-3 py-3 text-slate-600">
                          {c.indirizzo || "—"}
                        </td>
                        <td className="px-3 py-3 text-slate-600">
                          {FASCIA_CONSEGNA_LABELS[c.fascia_oraria] || c.fascia_oraria || "—"}
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-1.5">
                            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-[10px] font-semibold text-slate-600">
                              {(c.autista || "?").slice(0, 1).toUpperCase()}
                            </span>
                            <span className="truncate text-slate-700">{c.autista || "—"}</span>
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <Badge variant="outline" className={`text-[10px] ${st.className}`}>
                            {st.label}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {filtered.length > 0 ? (
            <div className="border-t border-slate-100 px-4 py-2 text-[11px] text-slate-400">
              Mostra {filtered.length} di {consegne.length} consegne · {formatDateLabel(data)}
            </div>
          ) : null}
        </section>

        {selected ? (
          <div className="xl:hidden">
            <ConsegnaDetailPanel
              c={selected}
              onClose={() => setSelectedId(null)}
              onStato={setStato}
              onEsito={esito}
            />
          </div>
        ) : null}

        <p className="text-[11px] text-slate-400">
          Percorso mappa opzionale via API <code>/farmacia/consegne/giri</code>. Sede in{" "}
          <Link to="/farmacia/impostazioni" className="underline">
            Impostazioni
          </Link>
          .
        </p>
      </div>

      <div className="hidden xl:block">
        <ConsegnaDetailPanel
          c={selected}
          onClose={() => setSelectedId(null)}
          onStato={setStato}
          onEsito={esito}
        />
      </div>
    </div>
  );
}
